import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL_ORDER_SERVICE } from '@/api/constant';
import ErrorMessage from '@/components/common/ErrorMessage';
import Layout from '@/components/Layout/Layout';
import Card from '@/components/UI/Card';
import FormField from '@/components/UI/FormField';
import { showToastNotification } from '@/utility/helper';
import { useEffect, useTransition } from 'react';
import { useForm } from 'react-hook-form';

const options = [
  { label: 'Active', value: 'active' },
  { label: 'Inactive', value: 'inactive' },
];

interface CheckoutRes {
  id: string;
  message: string;
  startDate: string;
  startTime: string;
  endDate: string;
  endTime: string;
  isActive: boolean | string;
  createdAt: string;
  updatedAt: string;
}

const CheckoutManagement = () => {
  const [isPending, startTransition] = useTransition();

  const {
    register,
    watch,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CheckoutRes>({
    defaultValues: {
      id: '',
      message: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      isActive: 'active',
    },
  });

  const fetchData = async () => {
    try {
      const res = await callApi<CheckoutRes[]>(
        `${API_URL_ORDER_SERVICE}/${API_ENDPOINT.checkoutMessage}`,
        { method: 'GET' },
      );
      reset({
        id: res && res.length ? res[0]?.id : '',
        message: res && res.length ? res[0]?.message : '',
        startDate: res && res.length ? res[0]?.startDate : '',
        endDate: res && res.length ? res[0]?.endDate : '',
        startTime: res && res.length ? res[0]?.startTime : '',
        endTime: res && res.length ? res[0]?.endTime : '',
        isActive: res[0]?.isActive ? 'active' : 'inactive',
      });
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const startDate = watch('startDate');
  const startTime = watch('startTime');
  const endDate = watch('endDate');

  const onSubmit = (data: CheckoutRes) => {
    try {
      startTransition(async () => {
        const res = await callApi(
          `${API_URL_ORDER_SERVICE}/${API_ENDPOINT.checkoutMessage}/${data.id}`,
          { method: 'PATCH', data },
        );
        if (res) {
          showToastNotification('success', 'Message updated successfully');
          fetchData();
        }
      });
    } catch (error) {
      ThrowError(error);
    }
  };

  return (
    <Layout>
      <Card>
        <h2 className="text-lg font-semibold mb-4">Checkout Message</h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <FormField
                label="Message"
                placeholder="Enter Message...."
                {...register('message', {
                  required: 'Message is required',
                })}
              />
              <ErrorMessage message={errors.message?.message} />
            </div>
            <div>
              <FormField
                label="Status"
                type="select"
                options={options}
                {...register('isActive', {
                  setValueAs: (value) => value === 'active',
                })}
              />
              <ErrorMessage message={errors.isActive?.message} />
            </div>

            <div>
              <FormField
                label="Start Date"
                type="date"
                {...register('startDate', {
                  required: 'Start date is required',
                })}
              />
              <ErrorMessage message={errors.startDate?.message} />
            </div>

            <div>
              <FormField
                label="End Date"
                type="date"
                {...register('endDate', {
                  required: 'End date is required',
                  validate: (value) => {
                    if (!startDate) return true;

                    return (
                      new Date(value) >= new Date(startDate) ||
                      'End date cannot be before start date'
                    );
                  },
                })}
              />
              <ErrorMessage message={errors.endDate?.message} />
            </div>

            <div>
              <FormField
                label="Start Time"
                type="time"
                {...register('startTime', {
                  required: 'Start time is required',
                })}
              />
              <ErrorMessage message={errors.startTime?.message} />
            </div>

            <div>
              <FormField
                label="End Time"
                type="time"
                {...register('endTime', {
                  required: 'End time is required',
                  validate: (value) => {
                    if (startDate === endDate) {
                      return (
                        value > startTime ||
                        'End time must be greater than start time when dates are same'
                      );
                    }
                    return true;
                  },
                })}
              />
              <ErrorMessage message={errors.endTime?.message} />
            </div>
          </div>
          <div className="flex justify-end mt-5">
            <button className="btn btn-primary" disabled={isPending}>
              Submit
            </button>
          </div>
        </form>
      </Card>
    </Layout>
  );
};

export default CheckoutManagement;
